// ABOUTME: Provides typed client wrappers for admin dashboard API endpoints.
import { ConfigPatchFields } from '../../../types/adminConfigPatch';
import { PollingPatchFields } from '../../../types/adminPollingPatch';
import { RegistrationConfigMap } from '../../../types/registration';
import axios from 'axios';

export type { ConfigPatchFields, PollingPatchFields };

const API_URL = '/v2/api/admin';

// Types
export interface Operator {
  id: string;
  name: string | null;
  pollingCount: number;
  configsCount: number;
  lastSyncAt: string | null;
}

export interface PollingItem {
  id: string;
  origin: string;
  entity: string;
  originOperatorId: string;
  originUserId?: string;
  lastSync: string | null;
  isDisabled: boolean;
  useTransmormer: boolean;
  url?: string;
  createdAt?: string;
  /** Origin-wide paired config count (includes peer-operator flows on a shared source). */
  configsCount?: number;
  /** Of configsCount, how many flows serve other TechLog operators on this shared source. */
  peerFlowsCount?: number;
  /** Distinct destination systems for the queried operator (primary chip; peers excluded). */
  destinations?: string[];
  operatorName?: string; // TechLog operator name for display
  daysToFetch?: number; // Days into the future to fetch data (e.g., FL3XX flights)
  registrations?: string[];
  simplifiedMaintenanceItem?: boolean;
  filterRegistrations?: boolean;
  customRegistrationCode?: string;
  [key: string]: unknown; // Allow any additional fields from database
}

export interface Config {
  id: string;
  origin: string;
  destination: string;
  entity: string;
  originOperatorId?: string;
  destinationOperatorId?: string;
  destinationUserId?: string;
  isDisabled: boolean;
  useTransmormer: boolean;
  useReleaseDate: boolean;
  useATASpec: boolean;
  skipUnlinkedTechlogFlights?: boolean;
  useSyncRecordAdapter?: boolean;
  /** Serialized (JsonSerializer) transformation schema, when one is stored. */
  schema?: string;
  operatorShortName?: string;
  preflightCustomFields?: string[];
  aircraftRegistration?: string;
  /** @deprecated Use `registrations` instead. */
  aircraftRegistrations?: string[];
  registrations?: RegistrationConfigMap;
  url?: string;
  createdAt?: string;
  syncCount?: number;
  lastSyncAt?: string | null;
  /** TechLog operator this flow belongs to, resolved server-side. */
  techlogOperatorId?: string | null;
  [key: string]: unknown; // Allow any additional fields from database
}

export interface SyncLog {
  id: string;
  origin: string;
  destination: string;
  originOpId: string;
  destinationOpId: string;
  entity: string;
  originEntityId: string;
  destinationEntityId: string | null;
  hash: string;
  lastSyncedAt: string;
  runIds: Array<{ runId: string; ts: string }>;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  createdAt: string;
  updatedAt: string;
  syncedData?: unknown;
}

export interface SyncedItem {
  id: string;
  origin: string;
  originId: string;
  destination: string;
  destinationId: string;
  type: string;
  originMetadata?: unknown;
  destinationMetadata?: unknown;
  createdAt?: string;
  updatedAt?: string;
}

// Helper to get auth headers
const getAuthHeaders = () => {
  const authData = localStorage.getItem('auth');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      if (parsed.token) {
        return {
          Authorization: `Bearer ${parsed.token}`,
        };
      }
    } catch (error) {
      console.error('Failed to parse auth data:', error);
    }
  }
  return {};
};

// Per-integration status contract shared with the TechLog integration card
// (TL-7343). Additive by design — extend with new fields, never repurpose.
export interface OperatorIntegrationStatus {
  operatorId: string;
  integration: string;
  enabled: boolean;
  lastSyncAt?: string;
}

export interface OperatorIntegrationUpdateResult {
  status: OperatorIntegrationStatus;
  pollingUpdated: number;
  configsUpdated: number;
  /** Polling rows left running because they still serve flows outside this integration. */
  sharedPollingSkipped: string[];
}

export interface OperatorIntegrationsUpdateResult {
  integrations: OperatorIntegrationStatus[];
  pollingUpdated: number;
  configsUpdated: number;
  /** Polling rows left running because they still serve peer operators' flows. */
  sharedPollingSkipped: string[];
}

export interface TechlogOperatorInfo {
  id: string;
  name: string;
  address?: string;
  phoneNumber?: string;
  aocNumber?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Operators API
export const operatorsApi = {
  getAll: async (): Promise<{ operators: Operator[] }> => {
    const response = await axios.get(`${API_URL}/operators`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getById: async (id: string): Promise<Operator> => {
    const response = await axios.get(`${API_URL}/operators/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getTechlogInfo: async (
    id: string,
  ): Promise<{ operator: TechlogOperatorInfo | null }> => {
    const response = await axios.get(
      `${API_URL}/operators/${id}/techlog-info`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  getAircraft: async (
    id: string,
  ): Promise<{
    aircraft: Array<{
      id: string;
      registration: string;
      operatorId: string;
      aircraftTypeId: string;
      serialNumber?: string;
      year?: number;
    }>;
  }> => {
    const response = await axios.get(`${API_URL}/operators/${id}/aircraft`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getIntegrations: async (
    id: string,
  ): Promise<{ integrations: OperatorIntegrationStatus[] }> => {
    const response = await axios.get(
      `${API_URL}/operators/${id}/integrations`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  updateIntegration: async (
    id: string,
    integration: string,
    data: { isDisabled: boolean },
  ): Promise<OperatorIntegrationUpdateResult> => {
    const response = await axios.patch(
      `${API_URL}/operators/${id}/integrations/${integration}`,
      data,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  updateAllIntegrations: async (
    id: string,
    data: { isDisabled: boolean },
  ): Promise<OperatorIntegrationsUpdateResult> => {
    const response = await axios.patch(
      `${API_URL}/operators/${id}/integrations`,
      data,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },
};

// Per-event email notification settings for one recipient. Keys are
// NotificationErrorType values; each entry toggles and throttles that event.
export type NotificationEventSetting = {
  enabled?: boolean;
  emailDelayMinutes?: number;
};

export interface NotificationRecipient {
  id: string;
  email: string;
  operatorId?: string;
  operatorName?: string;
  recipientRole: 'client' | 'internal';
  notificationSettings: Record<string, NotificationEventSetting>;
}

export interface CreateNotificationRecipientInput {
  email: string;
  operatorName?: string;
  recipientRole: 'client' | 'internal';
  notificationSettings: Record<string, NotificationEventSetting>;
}

export interface UpdateNotificationRecipientInput {
  recipientRole?: 'client' | 'internal';
  notificationSettings?: Record<string, NotificationEventSetting>;
}

// Per-operator email notification recipients and their settings
export const notificationSettingsApi = {
  list: async (
    operatorId: string,
  ): Promise<{ recipients: NotificationRecipient[] }> => {
    const response = await axios.get(
      `${API_URL}/operators/${operatorId}/notification-settings`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  create: async (
    operatorId: string,
    data: CreateNotificationRecipientInput,
  ): Promise<NotificationRecipient> => {
    const response = await axios.post(
      `${API_URL}/operators/${operatorId}/notification-settings`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  update: async (
    operatorId: string,
    subId: string,
    data: UpdateNotificationRecipientInput,
  ): Promise<NotificationRecipient> => {
    const response = await axios.put(
      `${API_URL}/operators/${operatorId}/notification-settings/${subId}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  remove: async (
    operatorId: string,
    subId: string,
  ): Promise<{ success: boolean }> => {
    const response = await axios.delete(
      `${API_URL}/operators/${operatorId}/notification-settings/${subId}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  history: async (
    operatorId: string,
  ): Promise<{ entries: ChangeHistoryEntry[] }> => {
    const response = await axios.get(
      `${API_URL}/operators/${operatorId}/notification-settings/history`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
};

// Polling API
export const pollingApi = {
  query: async (params: {
    operatorId?: string;
    origin?: string;
    entity?: string;
    isDisabled?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    polling: PollingItem[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await axios.get(`${API_URL}/polling`, {
      headers: getAuthHeaders(),
      params,
    });
    return response.data;
  },

  getById: async (id: string): Promise<PollingItem & { configs: Config[] }> => {
    const response = await axios.get(`${API_URL}/polling/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  update: async (
    id: string,
    data: PollingPatchFields,
  ): Promise<PollingItem> => {
    const response = await axios.patch(`${API_URL}/polling/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getConfigs: async (id: string): Promise<{ configs: Config[] }> => {
    const response = await axios.get(`${API_URL}/polling/${id}/configs`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
};

// Configs API
export const configsApi = {
  query: async (params: {
    origin?: string;
    destination?: string;
    entity?: string;
    originOperatorId?: string;
    destinationOperatorId?: string;
    isDisabled?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    configs: Config[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await axios.get(`${API_URL}/configs`, {
      headers: getAuthHeaders(),
      params,
    });
    return response.data;
  },

  getById: async (id: string): Promise<Config> => {
    const response = await axios.get(`${API_URL}/configs/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  update: async (id: string, data: ConfigPatchFields): Promise<Config> => {
    const response = await axios.patch(`${API_URL}/configs/${id}`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateSchema: async (id: string, schema: string): Promise<{ id: string }> => {
    const response = await axios.put(
      `${API_URL}/configs/${id}/schema`,
      { schema },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getSyncRecords: async (
    id: string,
  ): Promise<{
    syncLogs: SyncLog[];
    syncedItems: SyncedItem[];
    data: unknown[];
  }> => {
    const response = await axios.get(`${API_URL}/configs/${id}/sync-records`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
};

export interface ChangeHistoryEntry {
  id: string;
  targetCollection: string;
  targetDocumentId: string;
  documentPath: string;
  changeType: 'CREATE' | 'UPDATE' | 'DELETE' | 'ACCESS_DENIED';
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changedFields: string[];
  source: {
    source: 'USER' | 'SYSTEM';
    userId?: string;
    email?: string;
    name?: string;
    role?: string;
    ip?: string;
    userAgent?: string;
    workflowId?: string;
    runId?: string;
  };
  createdAt?: string;
}

// Per-document change history read from the changeLog collection
export const historyApi = {
  forDocument: async (
    collection: string,
    id: string,
  ): Promise<{ entries: ChangeHistoryEntry[] }> => {
    const response = await axios.get(`${API_URL}/history/${collection}/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
};

// Raw document access for the QueryBuilder edit modal (super_admin only)
export const documentsApi = {
  get: async (
    collection: string,
    id: string,
  ): Promise<Record<string, unknown> & { id: string }> => {
    const response = await axios.get(
      `${API_URL}/documents/${collection}/${id}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  update: async (
    collection: string,
    id: string,
    data: Record<string, unknown>,
  ): Promise<Record<string, unknown> & { id: string }> => {
    const response = await axios.put(
      `${API_URL}/documents/${collection}/${id}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
};

// Sync Records API
export const syncRecordsApi = {
  querySyncLogs: async (params: {
    origin?: string;
    destination?: string;
    originOpId?: string;
    destinationOpId?: string;
    entity?: string;
    originEntityId?: string;
    destinationEntityId?: string;
    action?: 'CREATE' | 'UPDATE' | 'DELETE';
    page?: number;
    limit?: number;
  }): Promise<{
    syncLogs: SyncLog[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await axios.get(`${API_URL}/sync-records/sync-logs`, {
      headers: getAuthHeaders(),
      params,
    });
    return response.data;
  },

  getSyncLogById: async (id: string): Promise<SyncLog> => {
    const response = await axios.get(
      `${API_URL}/sync-records/sync-logs/${id}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  querySyncedItems: async (params: {
    origin?: string;
    destination?: string;
    type?: string;
    originId?: string;
    destinationId?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    syncedItems: SyncedItem[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await axios.get(`${API_URL}/sync-records/synced-items`, {
      headers: getAuthHeaders(),
      params,
    });
    return response.data;
  },
};
